// STEP 6, `/you` — THE APP'S "LETTER" STEP, ITS GENERATE SCREEN (WEB-PARITY-5: YU-1 to YU-11, BL-4; ruling D3).
//
// The app's screen (`GenerateView`, AppealAce/Features/Generate/GenerateView.swift) laid out for a wide screen, in the
// app's order: "Generate your letter" and its body; the "PROppeal letter" card — or, where the case looks weak, the
// honest fork; the weak-support nudge; "Your case" (the honest check's verdict, the property, the assessed and requested
// figures, the reduction, the sales the letter cites, the grounds); the compliance card (the state's can-raise card,
// the two attestations, the disclaimer); "How should it sound?"; a letter that did not finish, as the app's error card;
// and the bar — the helper line, the generations left and "Generate appeal letter", or the fork's own bar.
//
// `/building` is this screen with the app's generation overlay drawn over it (`./Building.tsx`).
//
// COMING BACK TO A LETTER BEING WRITTEN (WEB-RESUME, R242; the website's counterpart of R187). The
// letter is written on the server, so an owner may leave the overlay — another step, Properties, the back button — and
// come back. This screen then reads the server's job for the letter the appeal is waiting on (the generation it had
// open, or the one the appeal's snapshot names while it is still unsettled) and goes where the answer says: still being
// written (`inFlight`, the server's own rule) → the overlay again; shipped → the letter; failed → the app's error card.
// A job the server no longer counts as being written gets the app's "We couldn't finish this letter" and "Try
// again". Generate is never the only thing on the screen while the letter is being written: it waits for
// that read, and the read opens the overlay. And one press is one attempt: its idempotency key is held until the server
// has answered it (`../../app/letterAttempt.ts`), so a retry of a request that never came back cannot start a second.
//
// WHAT THE SERVER DECIDES AND THIS PAGE ONLY PRINTS: the value, the ask, the reduction, the filed sales and the grounds
// (`v2Value`); whether the case looks weak, how many sales support it and whether the sales lead (`v2EvaluateCase`'s
// A-caseLooksWeak and A-compsLead), and the honest check on file — read, never run (`narrativeOnFile`, contract
// 2.23.0); the can-raise card and whether the acknowledgment is required (G-2); the attestations' words and hashes
// (`v2Credits`); the generations used (the snapshot's caps); a letter's failure, its sentence and its route
// (`v2GenerateLetter` `fetch`). The owner's choices — the tone and the two acts — go back on the queue.
//
// NO NAME AND ADDRESS HERE (D3, YU-1, YU-11). The app asks for contact info on Review & Export, before export, and sends
// its profile with every letter; so does the website — "Your contact info" is on `/letter`, held by this tab
// (`../../app/contact.ts`), and a letter request carries it when the owner has given it. Before that, a rebuild sends the
// letterhead the last letter was queued with, which the server holds; a first letter goes with none, as the app's does
// before its owner has filled the profile in.

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type {
  AppealSnapshot, AttestationCopy, CompView, LetterJobView, V2Result, ValueResponse, FetchLetterResponse,
} from "@contract";
import { lookupAddress } from "@v2-unit-address";
import { ownerOf, useContact } from "../../app/contact";
import { recordKeepingCorrections } from "../../app/corrections";
import { attemptAnswered, attemptKey } from "../../app/letterAttempt";
import { useAppealId, useSession } from "../../app/session";
import { useAction, useCall } from "../../app/useCall";
import { CopyLine, HeldLine, WithBold } from "../../components/copy";
import { heldText } from "../../components/heldCopy";
import { PaidRoute } from "../../components/PaidRoute";
import { Refusal } from "../../components/Refusal";
import { APP_VERSION } from "../assent";
import { canRaiseTitle } from "../nextStep";
import { BuildingOverlay } from "./Building";
import { Icon, type IconName } from "./parity4Parts";
import { assessedNote, verdictIcon, verdictTint } from "./parity4Rules";
import { say4, type Parity4WordingId } from "./parity4Wording";
import {
  basisTagged, failureIsQuestion, failureRows, forkBody, forkTitle, reductionValue, ROUTE_PATH, tidyStreet, toneBlurb,
  toneFrom, toneTitle, TONES, type ForkKind, type OverlayFacts, type RefusalRoute, type Tone,
} from "./generateRules";
import { sayG, sayGWith, type GenerateWordingId } from "./generateWording";

type Failed = Extract<LetterJobView, { status: "failed" }>;
type Reason = ValueResponse["body"]["grounds"]["reasons"][number];

const REASON_TITLE: Readonly<Record<Reason, { id: Parity4WordingId | GenerateWordingId; icon: IconName }>> = {
  overvaluation: { id: "reason.overvaluation", icon: "trend" },
  comparableSales: { id: "reason.comparableSales", icon: "house" },
  conditionIssues: { id: "reason.conditionIssues", icon: "wrench" },
  factualError: { id: "reason.factualError", icon: "doc" },
  uniformity: { id: "gen.reason.uniformity", icon: "scale" },
  incomeApproach: { id: "gen.reason.incomeApproach", icon: "house" },
};

const ROUTE_ICON: Readonly<Record<RefusalRoute, IconName>> = { comps: "house", condition: "wrench", value: "doc" };
const TONE_ICON: Readonly<Record<Tone, IconName>> = { formal: "doc", respectful: "hand", direct: "bolt" };

/** One of the app's sentences, stamped with its id, as every word this client holds is. */
function Said({
  id, as: Tag = "span", className, values,
}: {
  id: GenerateWordingId;
  as?: "h1" | "h2" | "h3" | "p" | "span" | "b" | "small" | "strong";
  className?: string;
  values?: Readonly<Record<string, string | number>>;
}) {
  return <Tag className={className} data-held-copy={id}>{values === undefined ? sayG(id) : sayGWith(id, values)}</Tag>;
}

function wordOf(id: Parity4WordingId | GenerateWordingId): string {
  return id.startsWith("gen.") ? sayG(id as GenerateWordingId) : say4(id as Parity4WordingId);
}

export function You() {
  const session = useSession();
  const appealId = useAppealId();
  return (
    <PaidRoute<ValueResponse>
      probe={() => session.transport.v2Value.compute({ appealId })}
      deps={[session.transport, appealId, session.revision]}
    >
      {(value, appeal) => <GenerateStep value={value} appeal={value.appeal ?? appeal} />}
    </PaidRoute>
  );
}

function GenerateStep({ value, appeal }: { value: ValueResponse; appeal: AppealSnapshot }) {
  const session = useSession();
  const appealId = useAppealId();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const overlayOpen = location.pathname === "/building";
  const generationAt = Number(params.get("generationAt") ?? "0");
  const failedAtParam = overlayOpen ? null : params.get("failedAt");
  const failedAt = failedAtParam === null ? null : Number(failedAtParam);
  const [tone, setTone] = useState<Tone>(() => toneFrom(params.get("tone")));
  const [adopt, setAdopt] = useState(false);
  const [ack, setAck] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const contact = useContact();

  const deps = [session.transport, appealId, session.revision];
  const account = useCall(() => session.transport.v2Credits.get(), [session.transport, session.revision]);
  // EVERY CORRECTION RESENT (WEB-PARITY-1C, F-WP1A-1): a bare `record` here put the county's figures back over the
  // owner's on every visit, before the letter was built from them.
  const resolution = useCall(() => recordKeepingCorrections(session.transport, appealId), deps);
  const comps = useCall(() => session.transport.v2SearchComps.edit({ appealId }), deps);
  // The check ON FILE, read and never run (`narrativeOnFile`, 2.23.0) — and the fork, its counts and whether the sales
  // lead, which this endpoint answers on every call.
  const check = useCall(() => session.transport.v2EvaluateCase({ appealId, narrativeOnFile: true }), deps);
  // The letterhead the last letter was queued with, for a rebuild before the owner has given contact in this tab.
  const lastShipped = appeal.letter.status === "shipped" ? appeal.letter.latestGenerationAt : null;
  const lastLetter = useCall<FetchLetterResponse | null>(
    () => (lastShipped === null
      ? Promise.resolve<V2Result<FetchLetterResponse | null>>({ ok: true, status: 200, response: null })
      : session.transport.v2GenerateLetter.fetch({ appealId, generationAt: lastShipped })),
    [...deps, lastShipped],
  );
  const failedJob = useCall<FetchLetterResponse | null>(
    () => (failedAt === null || !Number.isFinite(failedAt)
      ? Promise.resolve<V2Result<FetchLetterResponse | null>>({ ok: true, status: 200, response: null })
      : session.transport.v2GenerateLetter.fetch({ appealId, generationAt: failedAt })),
    [...deps, failedAt],
  );

  const queue = useAction(async (args: { adoptSha: string; ackSha: string | null }) => {
    const queued = lastLetter.status === "ok" && lastLetter.response !== null && lastLetter.response.body.job.status === "shipped"
      ? lastLetter.response.body.job.review?.queuedWith.owner ?? null
      : null;
    const owner = contact !== null
      ? ownerOf(contact)
      : queued !== null
        ? {
          name: queued.name, mailingAddress: queued.mailingAddress,
          ...(queued.email === null ? {} : { email: queued.email }), ...(queued.phone === null ? {} : { phone: queued.phone }),
        }
        : { name: "", mailingAddress: "" };
    const result = await session.transport.v2GenerateLetter.queue({
      appealId,
      // ONE KEY PER ATTEMPT (WEB-RESUME): the same key on a retry of a request that never came back, a new one once the
      // server has answered (F-WP5-1 stays fixed: a new press after a letter is a new letter).
      idempotencyKey: attemptKey(appealId, "build"),
      mode: "build",
      tone,
      owner,
      attestations: {
        adoptAsOwn: { variantSha256: args.adoptSha, clientTimestampMs: Date.now() },
        ...(args.ackSha === null ? {} : { canRaiseAck: { variantSha256: args.ackSha, clientTimestampMs: Date.now() } }),
      },
      appVersion: APP_VERSION,
    });
    attemptAnswered(appealId, "build", result);
    return result;
  });

  const onShipped = useCallback(() => navigate(`/letter?generationAt=${generationAt}`, { replace: true }), [navigate, generationAt]);
  const onFailed = useCallback((failed: Failed) => navigate(`/you?failedAt=${failed.generationAt}`, { replace: true }), [navigate]);
  // The server stopped counting the job as being written: the overlay closes onto this screen, which reads it back.
  const onStalled = useCallback(() => navigate("/you", { replace: true }), [navigate]);

  // ── THE LETTER THIS APPEAL IS WAITING ON (WEB-RESUME) ──
  // The newest generation this screen has had open (asked for here, or shown in the overlay), and — for an owner coming
  // back from anywhere else — the one the appeal's snapshot names while it is unsettled: the server's head says `queued`
  // until the first read of the job's ending, so a letter that finished while the owner was away is found here too.
  const [opened, setOpened] = useState<number | null>(null);
  useEffect(() => {
    if (overlayOpen && Number.isFinite(generationAt) && generationAt > 0) setOpened((g) => Math.max(g ?? 0, generationAt));
  }, [overlayOpen, generationAt]);
  const unsettled = appeal.letter.latestGenerationAt !== null
    && (appeal.letter.status === "queued" || appeal.letter.status === "generating")
    ? appeal.letter.latestGenerationAt
    : null;
  const waitingOn = opened === null ? unsettled : unsettled === null ? opened : Math.max(opened, unsettled);
  // Read whenever this screen is shown without the overlay or a failure on it — every arrival (`location.key`), since
  // the back button and the rail both land here with the job's state unknown.
  const checkAt = overlayOpen || failedAt !== null ? null : waitingOn;
  const resume = useCall<FetchLetterResponse | null>(
    () => (checkAt === null
      ? Promise.resolve<V2Result<FetchLetterResponse | null>>({ ok: true, status: 200, response: null })
      : session.transport.v2GenerateLetter.fetch({ appealId, generationAt: checkAt })),
    [...deps, checkAt, location.key],
  );
  const resumed = checkAt !== null && resume.status === "ok" && resume.response !== null
    && resume.response.body.job.generationAt === checkAt
    ? resume.response.body.job
    : null;
  const pendingJob = resumed !== null && (resumed.status === "queued" || resumed.status === "generating") ? resumed : null;
  const stalled = pendingJob !== null && !pendingJob.inFlight;
  // Generate waits for the answer, and is never pressable while the server says the letter is being written.
  const holdGenerate = (checkAt !== null && resume.status === "pending") || (pendingJob !== null && pendingJob.inFlight);
  useEffect(() => {
    if (resumed === null) return;
    if ((resumed.status === "queued" || resumed.status === "generating") && resumed.inFlight) {
      navigate(`/building?generationAt=${resumed.generationAt}&tone=${tone}`, { replace: true });
    } else if (resumed.status === "shipped") {
      navigate(`/letter?generationAt=${resumed.generationAt}`, { replace: true });
    } else if (resumed.status === "failed") {
      navigate(`/you?failedAt=${resumed.generationAt}`, { replace: true });
    }
  }, [resumed, navigate, tone]);

  // ── the facts, as served ──
  const v = value.body.value;
  const subject = appeal.subject;
  const state = subject?.state ?? null;
  const fractional = v.ratio.fractional;
  const evaluate = check.status === "ok" ? check.response : null;
  const weak = evaluate?.decisions["A-caseLooksWeak"];
  const fork: ForkKind | null = weak?.ran === true && weak.value.fork !== "none" ? weak.value.fork : null;
  const nearbySales = weak?.ran === true ? weak.value.nearbySales : 0;
  const compsLeadDecision = evaluate?.decisions["A-compsLead"];
  const compsLead = compsLeadDecision?.ran === true && compsLeadDecision.value.compsLead;
  const letterExists = appeal.letter.status === "shipped";
  // `weakSupported` (:137-141): no fork, nothing but the sales behind it, one to three of them, and no letter yet.
  const weakSupported = fork === null && weak?.ran === true && weak.value.independentGrounds.length === 0
    && weak.value.supportingComps >= 1 && weak.value.supportingComps <= 3 && !letterExists;
  const narrative = evaluate?.body.narrative ?? null;

  const compList: readonly CompView[] = comps.status === "ok" ? comps.response.body.comps : [];
  // THE SALES THE GENERATION WILL FILE (R184, GenerateView.swift:573-589): the server's `filing`, read off the size-adjusted
  // letter state under the rule the next generation is filed under — never the pre-letter `filedCompIds`, which judged a
  // sale on its raw price. An upper-range row is context, not support: its price is shown without the savings colour.
  const cited = v.filing.compIds.map((id) => compList.find((c) => c.compId === id)).filter((c): c is CompView => c !== undefined);
  const upperRange = new Set(v.filing.upperRangeCompIds);
  const ownerCompCount = compList.filter((c) => c.source === "owner" && c.usable).length;

  const g2 = resolution.status === "ok" ? resolution.response.decisions["G-2"] : null;
  // SILENCE NEVER DISARMS A GUARD: a G-2 that never ran requires the acknowledgment.
  const ackRequired = g2 === null || !g2.ran ? true : g2.value.requiresAcknowledgment;
  const canRaise = resolution.status === "ok" ? resolution.response.body.canRaise : null;

  const attestations: readonly AttestationCopy[] = account.status === "ok" ? account.response.body.legal.attestations : [];
  const variantId = ownerCompCount === 0 ? "base" : ownerCompCount === 1 ? "ownerComps.singular" : "ownerComps.plural";
  const adoptCopy = attestations.find((a) => a.kind === "adoptAsOwn" && a.variantId === variantId)
    ?? attestations.find((a) => a.kind === "adoptAsOwn");
  const adoptBase = attestations.find((a) => a.kind === "adoptAsOwn" && a.variantId === "base");
  const ackCopy = attestations.find((a) => a.kind === "canRaiseAck");
  const canCommit = adopt && adoptCopy !== undefined && (!ackRequired || (ack && ackCopy !== undefined));

  const caps = appeal.entitlement.caps.generations;
  const left = Math.max(0, caps.max - caps.used);
  const failed = failedJob.status === "ok" && failedJob.response !== null && failedJob.response.body.job.status === "failed"
    ? failedJob.response.body.job
    : null;

  const generate = async (): Promise<void> => {
    if (adoptCopy === undefined) return;
    const result = await queue.run({
      adoptSha: adoptCopy.sha256,
      ackSha: ackRequired && ackCopy !== undefined ? ackCopy.sha256 : null,
    });
    if (result.ok) {
      // `fresh`: this tab just asked for it, so the overlay runs its whole checklist even if the server is quick.
      navigate(`/building?generationAt=${result.response.body.queued.generationAt}&tone=${tone}`, { state: { fresh: true } });
    }
  };

  const facts: OverlayFacts | null = check.status === "ok"
    ? {
      usableComps: nearbySales,
      photos: value.body.photos.length,
      hasCondition: value.body.condition.note.trim() !== "",
      hasReasons: value.body.grounds.reasons.length > 0 || value.body.grounds.recordErrorNote.trim() !== "",
      tone,
    }
    : null;

  // The requested figure and its rows (:615-645).
  const requested = value.body.ask.requestedValue;
  const posture = value.decisions["V-4"];
  const raisesBill = posture.ran && posture.value.posture === "wouldNotLowerBill";
  const g6 = value.decisions["G-6"];
  const requestedAssessed = g6.ran ? g6.value.requestedAssessed : null;
  const conversion = requested === null ? null : assessedNote(requestedAssessed, v.ratio, state);
  const reduction = value.body.ask.estimatedReduction;
  const shownComps = expanded ? cited : cited.slice(0, 4);

  return (
    <div className="gen-step" data-step="generate">
      {/* UNDER THE OVERLAY THE STEP IS `inert` (WALK-FIX-4): covered, so nothing hidden takes the focus or a screen
          reader's attention; the header above the overlay stays the way out. */}
      <header className="step-hero" inert={overlayOpen}>
        <span className="hero-disc" aria-hidden="true"><Icon name="sparkles" size={24} /></span>
        <Said id="gen.title" as="h1" />
        {sayG("gen.body").split("\n\n").map((para, i) => (
          <p key={i} className="sub" data-held-copy="gen.body">{para}</p>
        ))}
      </header>

      <div className="gen-grid" inert={overlayOpen}>
        <div className="gen-col">
          {fork !== null ? (
            <section className="card gen-card gen-fork" data-fork={fork} aria-labelledby="gen-fork-title">
              <h2 className="gen-card-title" id="gen-fork-title">
                <Icon name="search" size={16} />
                <span data-held-copy={fork === "thinData" ? "gen.fork.thinData.title" : "gen.fork.noCase.title"}>{forkTitle(fork)}</span>
              </h2>
              <p className="gen-body" data-held-copy={fork === "thinData" ? "gen.fork.thinData.body" : "gen.fork.noCase.body"}>
                {forkBody(fork, nearbySales, state)}
              </p>
              <hr />
              <Said id="gen.fork.better" as="h3" className="gen-eyebrow" />
              <ul className="gen-points">
                <li><Icon name="wrench" size={14} /><Said id="gen.fork.point.condition" /></li>
                <li><Icon name="doc" size={14} /><Said id="gen.fork.point.record" /></li>
                <li><Icon name="search" size={14} /><Said id="gen.fork.point.sale" /></li>
              </ul>
            </section>
          ) : (
            <>
              <section className="card gen-card gen-path" data-path>
                <h2 className="gen-card-title">
                  <Icon name="sparkles" size={16} />
                  <Said id="gen.path.title" />
                  <Said id="gen.path.tag" className="gen-tag" />
                </h2>
                <Said id="gen.path.body" as="p" className="gen-small" />
              </section>
              {weakSupported && (
                <section className="card gen-card gen-weak" data-weak-support>
                  <Icon name="info" size={16} />
                  <div>
                    <Said id="gen.weak.body" as="p" className="gen-small" />
                    <button type="button" className="btn link" data-held-copy="gen.weak.add" onClick={() => navigate("/comps", { state: { addSale: true } })}>
                      {sayG("gen.weak.add")}
                    </button>
                  </div>
                </section>
              )}
            </>
          )}

          {/* "Your case" (YU-4): every row is the server's. */}
          <section className="card gen-card gen-case" data-your-case aria-labelledby="gen-case-title">
            <h2 className="gen-card-title gen-eyebrow" id="gen-case-title">
              <Icon name="doc" size={14} />
              <Said id="gen.case.title" />
            </h2>
            <Said id="gen.case.lead" as="p" className="gen-small" />
            {narrative !== null && !narrative.stale && (
              <div className={`gen-verdict tint-${verdictTint(narrative.verdict)}`} data-verdict-strip={narrative.verdict ?? ""}>
                <Icon name={verdictIcon(narrative.verdict)} size={18} />
                <div>
                  <Said id="gen.case.check" as="small" className="gen-eyebrow" />
                  <p className="gen-verdict-line">
                    {narrative.headline.trim() !== ""
                      ? narrative.headline
                      : narrative.verdict === null ? "" : say4(`verdict.title.${narrative.verdict}` as Parity4WordingId)}
                  </p>
                </div>
              </div>
            )}
            {narrative !== null && narrative.stale && (
              <p className="gen-stale" data-verdict-stale><Icon name="info" size={14} /><Said id="gen.case.stale" /></p>
            )}
            <dl className="gen-rows">
              <div className="gen-row-line">
                <Said id="gen.case.property" as="span" />
                <b data-row="property">{subject === null ? "" : lookupAddress(subject.address, subject.unit).trim()}</b>
              </div>
              {subject?.assessedValue != null && (
                <div className="gen-row-line">
                  <Said id="gen.case.assessed" as="span" />
                  <b data-row="assessed">{basisTagged(subject.assessedValue.value, fractional)}</b>
                </div>
              )}
              {requested !== null && (
                <>
                  <div className="gen-row-line">
                    {raisesBill
                      ? <Said id="gen.case.requestedRaises" as="span" values={{ note: sayG("gen.case.wouldNotLower") }} />
                      : <Said id="gen.case.requested" as="span" />}
                    <b
                      data-row="requested"
                      data-requested-assessed={requestedAssessed?.amount}
                      className={raisesBill ? "muted" : "savings"}
                    >
                      {basisTagged(requested, fractional)}
                    </b>
                  </div>
                  {conversion !== null && <p className="gen-conversion" data-row="conversion">{conversion}</p>}
                </>
              )}
              {reduction !== null && (
                <div className="gen-row-line">
                  <Said id={fractional ? "gen.case.reductionAssessed" : "gen.case.reduction"} as="span" />
                  <b data-row="reduction" className="savings">{reductionValue(reduction.amount, reduction.percent)}</b>
                </div>
              )}
            </dl>
            {compsLead && (
              <div className="gen-cited" data-cited={cited.length}>
                <p className="gen-row-line">
                  <Said id="gen.case.comps" as="span" />
                  <b>{cited.length}</b>
                </p>
                {cited.length === 0 ? (
                  <Said id="gen.case.noComps" as="p" className="gen-small" />
                ) : (
                  <>
                    <ul className="gen-comp-list">
                      {shownComps.map((c) => (
                        <li key={c.compId}>
                          <Icon name="house" size={12} />
                          <span className="gen-comp-address">{tidyStreet(c.address)}</span>
                          {c.salePrice !== null && (
                            upperRange.has(c.compId)
                              ? <b data-upper-range="">{basisTagged(c.salePrice, false)}</b>
                              : <b className="savings">{basisTagged(c.salePrice, false)}</b>
                          )}
                        </li>
                      ))}
                    </ul>
                    {cited.length > 4 && (
                      <button
                        type="button"
                        className="btn link"
                        aria-expanded={expanded}
                        data-held-copy={expanded ? "gen.case.fewer" : "gen.case.more"}
                        onClick={() => setExpanded((e) => !e)}
                      >
                        {expanded ? sayG("gen.case.fewer") : sayGWith("gen.case.more", { n: cited.length - 4 })}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="gen-grounds" data-grounds={value.body.grounds.reasons.join(",")}>
              <Said id="gen.case.grounds" as="p" className="gen-row-label" />
              {value.body.grounds.reasons.length === 0 ? (
                <Said id="gen.case.noGrounds" as="p" className="gen-small" />
              ) : (
                <ul className="gen-ground-list">
                  {value.body.grounds.reasons.map((r) => (
                    <li key={r}>
                      <Icon name={REASON_TITLE[r].icon} size={12} />
                      <span data-held-copy={REASON_TITLE[r].id}>{wordOf(REASON_TITLE[r].id)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        <div className="gen-col">
          {/* The compliance card (YU-5): G-2's card, the two acts, the disclaimer. */}
          <section className="card gen-card gen-compliance" data-compliance={canRaise?.risk ?? "unknown"}>
            {canRaise !== null && canRaise.card !== null && (
              <div className="gen-compliance-head">
                <span className={`gen-risk risk-${canRaise.risk}`} aria-hidden="true">
                  <Icon name={canRaise.risk === "cannotRaiseAtBoard" ? "shield" : canRaise.risk === "unknown" ? "question" : "warn"} size={18} />
                </span>
                <div>
                  {canRaiseTitle(canRaise.card.code, state) !== null && (
                    <h2 className="gen-compliance-title">{canRaiseTitle(canRaise.card.code, state)}</h2>
                  )}
                  <CopyLine copy={canRaise.card} className="gen-small" />
                </div>
              </div>
            )}
            {account.status === "failed" && <Refusal failure={account.failure} context="The attestations" />}
            {ackRequired && (ackCopy === undefined ? (
              account.status === "ok" && <HeldLine id="assent.canRaiseAck" />
            ) : (
              <label className="toggle gen-toggle">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.currentTarget.checked)} />
                <span className="attest" data-copy-code={ackCopy.code} data-version={ackCopy.version} data-variant={ackCopy.variantId}>
                  <WithBold text={ackCopy.text} />
                </span>
              </label>
            ))}
            {adoptCopy === undefined ? (
              account.status === "ok" && <HeldLine id="assent.adoptAsOwn" />
            ) : (
              <>
                <label className="toggle gen-toggle">
                  <input type="checkbox" checked={adopt} onChange={(e) => setAdopt(e.currentTarget.checked)} />
                  {/* THE SERVED WORDS, AS THE APP LAYS THEM OUT: the act's own sentence beside the switch, and — where
                      the owner added sales — the sentence about them beneath it (GenerateView.swift:1148-1159). The act
                      cites the variant the owner read, both sentences together. */}
                  <span className="attest" data-copy-code={adoptCopy.code} data-version={adoptCopy.version} data-variant={adoptCopy.variantId}>
                    <WithBold text={adoptBase !== undefined && adoptCopy.text.startsWith(adoptBase.text) ? adoptBase.text : adoptCopy.text} />
                  </span>
                </label>
                {adoptBase !== undefined && adoptCopy.variantId !== "base" && adoptCopy.text.startsWith(adoptBase.text) && (
                  <p className="gen-small gen-addendum" data-owner-comps-addendum>{adoptCopy.text.slice(adoptBase.text.length).trim()}</p>
                )}
              </>
            )}
            <Said id="gen.disclaimer" as="p" className="gen-disclaimer" />
          </section>

          {fork === null && (
            <section className="card gen-card gen-tone" aria-labelledby="gen-tone-title">
              <h2 className="gen-card-title gen-eyebrow" id="gen-tone-title">
                <Icon name="pencil" size={14} />
                <Said id="gen.tone.title" />
              </h2>
              <div className="gen-tiles" role="radiogroup" aria-labelledby="gen-tone-title">
                {TONES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={tone === t}
                    className={tone === t ? "gen-tile on" : "gen-tile"}
                    data-tone={t}
                    onClick={() => setTone(t)}
                  >
                    <Icon name={TONE_ICON[t]} size={18} />
                    <span>{toneTitle(t)}</span>
                  </button>
                ))}
              </div>
              <p className="gen-small gen-blurb" data-tone-blurb={tone}>{toneBlurb(tone)}</p>
            </section>
          )}

          {check.status === "failed" && <Refusal failure={check.failure} context="Your case" />}
          {comps.status === "failed" && <Refusal failure={comps.failure} context="Your comparable sales" />}
          {failed !== null && <FailureCard failed={failed} onRoute={(r) => navigate(ROUTE_PATH[r])} />}
          {/* A fault on our side gets the app's heading for a letter that did not finish, not a refusal's (WALK-FIX-1). */}
          {failedJob.status === "failed" && (
            <Refusal failure={failedJob.failure} context="Your letter" faultHeading={<Said id="gen.error.title" as="h1" />} />
          )}
          {queue.state.status === "failed" && (
            <Refusal failure={queue.state.failure} context="Building your letter" faultHeading={<Said id="gen.error.title" as="h1" />} />
          )}
          {/* WEB-RESUME: the letter this appeal was waiting on could not be read back — said, never silently Generate. */}
          {checkAt !== null && resume.status === "failed" && (
            <Refusal failure={resume.failure} context="Your letter" faultHeading={<Said id="gen.error.title" as="h1" />} />
          )}
          {/* WEB-RESUME: a job the server no longer counts as being written — the app's card for a letter that did not
              finish in time (GenerateView.swift's error card, its title only), and "Try again" below. */}
          {stalled && pendingJob !== null && (
            <section className="card gen-card gen-error" role="alert" data-letter-stalled={pendingJob.generationAt}>
              <div className="gen-error-head">
                <Icon name="warn" size={18} />
                <div>
                  <Said id="gen.error.title" as="h2" className="gen-error-title" />
                </div>
              </div>
            </section>
          )}

          {fork !== null ? (
            <div className="gen-bar gen-fork-bar" data-fork-bar={fork}>
              {fork === "thinData" ? (
                <>
                  <button type="button" className="btn primary" data-held-copy="gen.forkBar.addSale" onClick={() => navigate("/comps", { state: { addSale: true } })}>
                    {sayG("gen.forkBar.addSale")}
                  </button>
                  <button type="button" className="btn ghost" data-held-copy="gen.forkBar.addReasons" onClick={() => navigate("/condition")}>
                    {sayG("gen.forkBar.addReasons")}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn primary" data-held-copy="gen.forkBar.addReasons" onClick={() => navigate("/condition")}>
                    {sayG("gen.forkBar.addReasons")}
                  </button>
                  <button type="button" className="btn ghost" data-held-copy="gen.forkBar.lowerSale" onClick={() => navigate("/comps", { state: { addSale: true } })}>
                    {sayG("gen.forkBar.lowerSale")}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn quiet"
                data-held-copy="gen.forkBar.fileAnyway"
                disabled={!canCommit || queue.state.status === "pending" || holdGenerate}
                onClick={() => void generate()}
              >
                {sayG("gen.forkBar.fileAnyway")}
              </button>
              {!canCommit && <Said id="gen.forkBar.confirm" as="p" className="gen-small gen-center" />}
            </div>
          ) : (
            <div className="gen-bar" data-generate-bar>
              <Said id={canCommit ? "gen.helper" : "gen.confirm"} as="p" className="gen-small gen-center" />
              {caps.used > 0 && left > 0 && (
                <Said id="gen.left" as="p" className="gen-small gen-center" values={{ left, max: caps.max }} />
              )}
              <button
                type="button"
                className="btn primary gen-generate"
                data-held-copy={failed !== null || stalled ? "gen.retry" : "gen.generate"}
                disabled={!canCommit || queue.state.status === "pending" || holdGenerate}
                onClick={() => void generate()}
              >
                {sayG(failed !== null || stalled ? "gen.retry" : "gen.generate")}
              </button>
            </div>
          )}
        </div>
      </div>

      {overlayOpen && (
        <BuildingOverlay
          generationAt={generationAt}
          facts={facts}
          fresh={(location.state as { fresh?: unknown } | null)?.fresh === true}
          onShipped={onShipped}
          onFailed={onFailed}
          onStalled={onStalled}
        />
      )}
    </div>
  );
}

/** "We couldn't finish this letter" — or, where the refusal has an answer, "Is there something we don't know about your
 *  home?" — with the server's sentence and the app's routes to where the fix lives (GenerateView.swift:966-1041). */
function FailureCard({ failed, onRoute }: { failed: Failed; onRoute: (route: RefusalRoute) => void }) {
  const question = failureIsQuestion(failed.failure);
  const rows = failureRows(failed.failure);
  return (
    // No "CODE · status" line under the sentence (WEB-PARITY-1B, BL-2): both stay on the element.
    <section
      className={question ? "card gen-card gen-error question" : "card gen-card gen-error"}
      role="alert"
      data-letter-failure-code={failed.failure.code}
      data-letter-failure-status={failed.failure.httpStatus}
      data-route={failed.failure.route}
    >
      <div className="gen-error-head">
        <Icon name={question ? "question" : "warn"} size={18} />
        <div>
          <Said id={question ? "gen.error.question" : "gen.error.title"} as="h2" className="gen-error-title" />
          <p className="gen-error-message" data-copy-code={failed.failure.copy.code}>{failed.failure.copy.text}</p>
        </div>
      </div>
      {rows.length > 0 && (
        <>
          <hr />
          {question
            ? <Said id="gen.error.questionLead" as="p" className="gen-small" />
            : <HeldLine id="letter.routes.heading" className="gen-small" />}
          <div className="routes">
            {rows.map((route) => (
              <button key={route} className="route" type="button" data-app-route={route} onClick={() => onRoute(route)}>
                <Icon name={ROUTE_ICON[route]} size={16} />
                <span className="route-words">
                  <b data-held-copy={`letter.routes.${route}.title`}>{heldText(`letter.routes.${route}.title`)}</b>
                  <span data-held-copy={`letter.routes.${route}.detail`}>{heldText(`letter.routes.${route}.detail`)}</span>
                </span>
                <Icon name="chevron" size={14} />
              </button>
            ))}
          </div>
          {question && <Said id="gen.error.floor" as="p" className="gen-small gen-floor" />}
        </>
      )}
    </section>
  );
}
